/**
 * Initializes an Alpine.js data object for a product explorer with Swiper carousels.
 * Key features include:
 * 1. Swiper Integration: Utilizes Swiper to create responsive carousels for product collections and recommendations.
 * 2. Dynamic Data Fetching: Asynchronously loads and displays products based on collection or recommendation type.
 * 3. Responsive Adaptations: Configures carousels to adjust to browser size changes and ensures a consistent user experience.
 */

const classes = {
  wrapperCenter: 'swiper-wrapper--center',
};

const productExplorer = (numSlides) => ({
  numSlides: numSlides || 10,
  selectedIdx: 0,
  carouselData: [],

  /**
   * Centers the wrapper swiper fully fits in container.
   */
  swiperCenterOnNoScroll: (swiper) => {
    const isSwipeable = swiper.virtualSize > swiper.size;

    if (!isSwipeable) {
      swiper.wrapperEl?.classList.add(classes.wrapperCenter);
    } else {
      swiper.wrapperEl?.classList.remove(classes.wrapperCenter);
    }
  },

  get swiperTabsConfig() {
    return {
      slidesPerView: 'auto',
      freeMode: {
        momentum: false,
      },
      mousewheel: {
        forceToAxis: true,
      },
      on: {
        ppClick: (_, event) => {
          event.target.dispatchEvent(new CustomEvent('swiper-tap', { bubbles: true }));
        },
        init: this.swiperCenterOnNoScroll,
        resize: this.swiperCenterOnNoScroll,
      },
    };
  },

  get swiperConfig() {
    return {
      slidesPerView: 'auto',
      autoHeight: false,
      navigation: {
        prevEl: '.swiper-button-prev-prod-explorer',
        nextEl: '.swiper-button-next-prod-explorer',
        disabledClass: 'swiper-button-hidden',
      },
      mousewheel: {
        forceToAxis: true,
      },
      on: {
        init: () => {
          this.swiperCenterOnNoScroll;
          this.equalizeCardHeights();
        },
        resize:() => {
          this.swiperCenterOnNoScroll;
          this.equalizeCardHeights();
        },
      },
    };
  },

  equalizeCardHeights() {
    const cards = this.$el.querySelectorAll('.product-card--link-card');
    let maxHeight = 0;
  
    cards.forEach(card => {
      card.style.paddingBottom = '';
    });
  
    cards.forEach(card => {
      if (card.offsetHeight > maxHeight) {
        maxHeight = card.offsetHeight;
      }
    });
  
    cards.forEach(card => {
      const currentHeight = card.offsetHeight;
      const difference = maxHeight - currentHeight;
      if (difference > 0) {
        card.style.paddingBottom = `${difference}px`;
      }
    });
  },

  get selectedProducts() {
    const foundCarousel = this.carouselData[this.selectedIdx];
    const firstNumProducts = foundCarousel?.results?.slice(0, this.numSlides) || [];
    return firstNumProducts;
  },

  get selectedCollectionHandle() {
    return this.carouselData[this.selectedIdx]?.handle;
  },

  get selectedCollectionTitle() {
    return this.carouselData[this.selectedIdx]?.title;
  },

  get selectedCollectionUrl() {
    return this.carouselData[this.selectedIdx]?.url;
  },

  cacheNodes() {
    this.swiperTabsEl = this.$el.querySelector('.swiper[swiper-tab-bank]');
    this.swiperContentEls = this.$el.querySelectorAll('.swiper:not([swiper-tab-bank])');
  },

  /**
   * Fetch carousel data from Searchspring API.
   * - Currently only supports fetching either collections data or recommendations, not both
   * - This could be extended to support some tabs being collections and others being
   *   product recommendations.
   */
  async getCarouselData() {
    let carouselData = [];

    /**
     * Map the swiper element to an object containing data about the carousel.
     */
    const mapSSData = (swiper) => {
      const type = swiper.dataset.type;
      return {
        type,
        results: [],
        ...(type === 'products_by_collection' && {
          handle: swiper.dataset.collectionHandle,
          title: swiper.dataset.collectionTitle,
          url: swiper.dataset.collectionUrl,
        }),
        ...(type === 'product_recommendations' && {
          intent: swiper.dataset.recommendationsIntent,
          productId: swiper.dataset.recommendationsProductId,
        }),
      };
    };

    carouselData = Array.from(this.swiperContentEls).map(mapSSData);

    const productRequests = carouselData.map((entry) =>
      entry.type === 'products_by_collection'
        ? fetchCollection(entry.handle, 10)
        : entry.type === 'product_recommendations'
          ? fetchRecommendedProducts(entry.intent, `gid://shopify/Product/${entry.productId}`)
          : null,
    );
    await Promise.all(productRequests).then((responses) =>
      responses.forEach((response, idx) => {
        carouselData[idx].results = response?.products;
      }),
    );
    // fetch collection data for carousels in here.
    return carouselData;
  },

  async setData() {
    this.carouselData = await this.getCarouselData();
  },

  buildSwipers() {
    // Tabs setup
    if (this.swiperTabsEl) {
      this.swiperTabsInstance = new Swiper(this.swiperTabsEl, this.swiperTabsConfig);
    }

    // Content setup
    if (this.swiperContentEls) {
      this.swiperContentInstances = Array.from(this.swiperContentEls).map(
        (swiperEl) => new Swiper(swiperEl, this.swiperConfig),
      );
    }
  },

  async init() {
    this.cacheNodes();
    await this.setData();
    this.buildSwipers();
  },
});

document.addEventListener('alpine:init', () => {
  Alpine.data('productExplorer', productExplorer);
});
